import { useEffect, useRef } from 'react';

import {
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryResult,
  hashKey,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import {
  MAX_EMPTY_PAGE_AUTO_FETCHES,
  appendIncludeEvents,
  isFeedLoading,
  shouldAutoFetchNextPage,
} from '@splitfiesta/core';
import type { ActivityFeedItemDto } from '@splitfiesta/shared';

import { useActivityFeedKeyParts } from '@/hooks/queries/activityFeedKey';
import { useTypedApi } from '@/hooks/useTypedApi';

interface ActivityFeedParams {
  limit?: number;
  groupId?: string;
  cursor?: string;
}

interface ActivityFeedResponse {
  activities: ActivityFeedItemDto[];
  pagination: {
    hasMore: boolean;
    nextCursor?: string;
    limit: number;
  };
}

// A short read budget server-side can end a page early, even with nothing in it.
const getNextPageParam = (lastPage: ActivityFeedResponse) =>
  lastPage.pagination.hasMore && lastPage.pagination.nextCursor
    ? lastPage.pagination.nextCursor
    : undefined;

/**
 * An empty page with more behind it leaves nothing to scroll to, so the
 * feed pulls the next one itself, a bounded number of times.
 */
function useAutoFetchPastEmptyPages(
  query: UseInfiniteQueryResult<InfiniteData<ActivityFeedResponse>>,
  queryKey: QueryKey
) {
  const { data, hasNextPage, isFetching, isError, isSuccess, fetchNextPage } =
    query;
  const lastAutoFetchedAt = useRef<number | null>(null);
  const hadError = useRef(false);
  const keyHash = hashKey(queryKey);

  useEffect(() => {
    lastAutoFetchedAt.current = null;
  }, [keyHash]);

  // A recovered query gets its auto-fetches back.
  useEffect(() => {
    if (isError) {
      hadError.current = true;
    } else if (isSuccess && hadError.current) {
      hadError.current = false;
      lastAutoFetchedAt.current = null;
    }
  }, [isError, isSuccess]);

  useEffect(() => {
    if (!hasNextPage || isFetching) return;
    if (
      !shouldAutoFetchNextPage(
        data?.pages,
        lastAutoFetchedAt.current,
        MAX_EMPTY_PAGE_AUTO_FETCHES
      )
    ) {
      return;
    }
    lastAutoFetchedAt.current = data?.pages.length ?? null;
    void fetchNextPage({ cancelRefetch: false });
  }, [data, hasNextPage, isFetching, fetchNextPage]);
}

/**
 * Fetch activity feed with infinite scroll using CURSOR-BASED pagination
 */
export function useActivityFeedInfiniteQuery(
  params?: ActivityFeedParams,
  enabled = true
) {
  const api = useTypedApi();
  const { includeEvents, flagsReady, keyFor } = useActivityFeedKeyParts();

  const queryKey = keyFor(params as Record<string, unknown>);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const queryParams = new URLSearchParams();

      // Add cursor if we have one (for next pages)
      if (pageParam) {
        queryParams.append('cursor', pageParam);
      }

      // Add other params
      if (params?.limit !== undefined) {
        queryParams.append('limit', String(params.limit));
      }
      if (params?.groupId !== undefined) {
        queryParams.append('groupId', params.groupId);
      }
      appendIncludeEvents(queryParams, includeEvents);

      const queryString = queryParams.toString();
      const response = await api.get(
        `/activity/feed${queryString ? `?${queryString}` : ''}`
      );

      // Extract data from success response wrapper
      const responseData = response as unknown as {
        data: ActivityFeedResponse;
      };
      return responseData.data;
    },
    getNextPageParam,
    initialPageParam: undefined as string | undefined,
    enabled: enabled && flagsReady,
    // Always fetch fresh data - no caching for activity feed
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  useAutoFetchPastEmptyPages(query, queryKey);

  return {
    ...query,
    queryKey,
    isLoading: isFeedLoading(query, enabled, flagsReady),
  };
}

/**
 * Fetch activity feed (non-infinite) - single page query
 */
export function useActivityFeedQuery(
  params?: ActivityFeedParams,
  enabled = true
) {
  const api = useTypedApi();
  const { includeEvents, flagsReady, keyFor } = useActivityFeedKeyParts();

  const query = useQuery({
    queryKey: keyFor(params as Record<string, unknown>),
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (params?.cursor) {
        queryParams.append('cursor', params.cursor);
      }
      if (params?.limit !== undefined) {
        queryParams.append('limit', String(params.limit));
      }
      if (params?.groupId !== undefined) {
        queryParams.append('groupId', params.groupId);
      }
      appendIncludeEvents(queryParams, includeEvents);

      const queryString = queryParams.toString();
      const response = await api.get(
        `/activity/feed${queryString ? `?${queryString}` : ''}`
      );

      // Extract data from success response wrapper
      const responseData = response as unknown as {
        data: ActivityFeedResponse;
      };
      return responseData.data;
    },
    enabled: enabled && flagsReady,
    // Always fetch fresh data - no caching for activity feed
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  return { ...query, isLoading: isFeedLoading(query, enabled, flagsReady) };
}

/**
 * Fetch group-specific activity feed with COMPLETE HISTORY (no TTL limitation)
 * Uses new endpoint that queries expenses directly instead of activity feed table
 * This ensures all-time activity history is available for group activity tab
 */
export function useGroupActivityFeedInfiniteQuery(
  groupId: string,
  params?: Omit<ActivityFeedParams, 'groupId'>,
  enabled = true
) {
  const api = useTypedApi();
  const { includeEvents, flagsReady, keyFor } =
    useActivityFeedKeyParts(groupId);

  const queryKey = keyFor(params as Record<string, unknown>);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const queryParams = new URLSearchParams();

      // Add cursor if we have one (for next pages)
      if (pageParam) {
        queryParams.append('cursor', pageParam);
      }

      // Add other params
      if (params?.limit !== undefined) {
        queryParams.append('limit', String(params.limit));
      }
      appendIncludeEvents(queryParams, includeEvents);

      const queryString = queryParams.toString();
      const response = await api.get(
        `/groups/${groupId}/activity${queryString ? `?${queryString}` : ''}`
      );

      // Extract data from success response wrapper
      const responseData = response as unknown as {
        data: ActivityFeedResponse;
      };
      return responseData.data;
    },
    getNextPageParam,
    initialPageParam: undefined as string | undefined,
    enabled: enabled && flagsReady,
    // Always fetch fresh data - no caching for activity feed
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  useAutoFetchPastEmptyPages(query, queryKey);

  return {
    ...query,
    queryKey,
    isLoading: isFeedLoading(query, enabled, flagsReady),
  };
}
